import {
  ArrowDropDown,
  ContentCopy,
  ElectricalServices,
  InsertLink,
} from "@mui/icons-material";
import {
  ButtonGroup,
  ClickAwayListener,
  CircularProgress,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import ConnectButton from "features/dataConnectors/components/ConnectButton";
import { OAUTH_REDIRECT_URL } from "features/auth/constants";
import { IConnectButtonProps } from "features/dataConnectors/types";
import { motion } from "motion/react";
import {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { authService, syncService } from "Apis";
import { useNotification, useSelectedProject } from "Hooks";
import { useConfirmationContext } from "Providers/ConfirmationProvider";
import { ApiShareLinkType, SyncServiceUrlServiceEnum } from "@incendium/api";

function ShopifyConnectButton({
  oauthClientId,
  service,
  syncClients,
  setSyncClients,
  state,
  linkAccountProjectId,
  onConnected,
}: IConnectButtonProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [isCreatingLink, setIsCreatingLink] = useState(false);
  const [dialogMode, setDialogMode] = useState<"connect" | "share">("connect");

  const { selectedProject } = useSelectedProject();
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const { openConfirmation, closeConfirmation } = useConfirmationContext();

  const handleClick = useCallback(() => {
    setDialogMode("connect");
    setOpen(true);
  }, []);

  const handleToggle = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback((event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setMenuOpen(false);
  }, []);

  const convertToMyShopifyDomain = (input) => {
    if (!input) return "";
    let storeName = input.trim().toLowerCase();
    storeName = storeName.replace(/^(https?:\/\/)?(www\.)?/, "");
    storeName = storeName.replace(/\.myshopify\.com$/, "");
    storeName = storeName.replace(/\s+/g, "-");

    return storeName + ".myshopify.com";
  };

  const shop = useMemo(() => {
    if (!name) {
      return "";
    }
    return convertToMyShopifyDomain(name);
  }, [name]);

  const isDisabled = useMemo(() => {
    return !shop.includes("myshopify.com");
  }, [shop]);

  const handleConnected = useCallback((oauthClient) => {
    setOpen(false);
    setName("");
    if (onConnected) {
      onConnected(oauthClient);
    }
  }, [onConnected]);

  const redirectBase = useMemo(() => OAUTH_REDIRECT_URL, []);

  const handleCreateConnectLink = useCallback(async () => {
    if (!selectedProject?.id || !selectedProject?.clientId) {
      showErrorNotification("Select a project before creating a connect link.");
      return;
    }

    if (!shop || isDisabled) {
      showErrorNotification("Enter a valid shop name before creating a connect link.");
      return;
    }

    setIsCreatingLink(true);
    try {
      const shareToken = await authService.authShareToken({
        body: {
          clientId: selectedProject.clientId as number,
          projectId: selectedProject.id as number,
          type: ApiShareLinkType.SHARE_LINK_TYPE_SYNC,
        },
      });

      if (!shareToken?.token) {
        throw new Error("Missing share token");
      }

      const shareRedirectUrl = (() => {
        const tokenPath = encodeURIComponent(shareToken.token);
        try {
          const url = new URL(redirectBase);
          url.pathname = `${url.pathname.replace(/\/$/, "")}/${tokenPath}`;
          return url.toString();
        } catch {
          return `${redirectBase.replace(/\/$/, "")}/${tokenPath}`;
        }
      })();

      const res = await syncService.syncServiceUrl({
        service: service as unknown as SyncServiceUrlServiceEnum,
        redirectUrl: shareRedirectUrl || "",
        state: shareRedirectUrl || "",
        shop,
      });

      if (!res?.authUrl) {
        throw new Error("Missing auth URL");
      }


      openConfirmation({
        title: "Share this link",
        body: (
          <Stack spacing={2}>
            <DialogContentText>
              Share this link with someone who has access to your Shopify store but
              does not use Incendium. They can complete the Shopify connection on
              your behalf and it will be added to your Incendium account.
            </DialogContentText>
            <Button
              variant="outlined"
              onClick={() => {
                navigator.clipboard.writeText(res.authUrl || "");
                showSuccessNotification("Link copied to clipboard.");
              }}
              endIcon={<ContentCopy />}
            >
              Copy Link
            </Button>
            <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
              {res.authUrl}
            </Typography>
          </Stack>
        ),
        callback: async () => {
          closeConfirmation();
        },
      });
      setOpen(false);
      setName("");
    } catch (error) {
      showErrorNotification("Failed to create connect link.");
    } finally {
      setIsCreatingLink(false);
      setMenuOpen(false);
    }
  }, [
    selectedProject,
    showErrorNotification,
    service,
    redirectBase,
    openConfirmation,
    closeConfirmation,
    showSuccessNotification,
    shop,
    isDisabled,
  ]);

  const handleMenuItemClick = useCallback(
    async (event: ReactMouseEvent<HTMLLIElement>) => {
      event.preventDefault();
      setMenuOpen(false);
      setDialogMode("share");
      setOpen(true);
    },
    []
  );

  const buttonLabel = useMemo(
    () => (oauthClientId ? "Reconnect" : "Connect"),
    [oauthClientId]
  );

  return (
    <>
      <ButtonGroup
        variant="outlined"
        size="small"
        ref={anchorRef}
        component={motion.div}
        initial={{
          opacity: 0,
          scale: 0.9,
        }}
        animate={{
          opacity: 1,
          scale: 1,
        }}
      >
        <Button
          startIcon={<ElectricalServices />}
          onClick={() => {
            setMenuOpen(false);
            handleClick();
          }}
        >
          {buttonLabel}
        </Button>
        <Button
          size="small"
          aria-controls={menuOpen ? "shopify-connect-split-menu" : undefined}
          aria-expanded={menuOpen ? "true" : undefined}
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          <ArrowDropDown />
        </Button>
      </ButtonGroup>

      <Popper
        open={menuOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        placement="bottom-end"
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  id="shopify-connect-split-menu"
                  autoFocusItem
                  sx={{ minWidth: 220 }}
                >
                  <MenuItem
                    onClick={handleMenuItemClick}
                    disabled={isCreatingLink}
                  >
                    <Tooltip
                      title="Generate a link you can share with someone outside Incendium to connect their Shopify store."
                      placement="left"
                      arrow
                    >

                      <Stack direction="row" spacing={1} alignItems="center">
                        {isCreatingLink ? (
                          <CircularProgress size={16} />
                        ) : (
                          <InsertLink fontSize="small" />
                        )}
                        <Typography variant="body2">
                          {isCreatingLink ? "Creating link..." : "Create Connect Link"}
                        </Typography>
                      </Stack>
                    </Tooltip>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <CustomDialog open={open} fullWidth maxWidth="sm">
        <DialogContent>
          <Typography mb={2}>
            Enter your Shop Name. You can find it under{" "}
            <b>{"Settings > Store details"}</b> in your Shopify Admin.
          </Typography>
          <TextField
            fullWidth
            label="Shop Name"
            name="shop"
            value={name}
            variant="outlined"
            onChange={(e) => setName(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button
            color="secondary"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
          {dialogMode === "share" ? (
            <Button
              variant="contained"
              onClick={handleCreateConnectLink}
              disabled={isDisabled || isCreatingLink}
              startIcon={
                isCreatingLink ? <CircularProgress size={18} color="inherit" /> : <InsertLink />
              }
            >
              {isCreatingLink ? "Creating" : "Create Link"}
            </Button>
          ) : (
            <ConnectButton
              service={service}
              syncClients={syncClients}
              setSyncClients={setSyncClients}
              state={state}
              shop={shop}
              linkAccountProjectId={linkAccountProjectId}
              disabled={isDisabled}
              onConnected={handleConnected}
            />
          )}
        </DialogActions>
      </CustomDialog>
    </>
  );
}

export default ShopifyConnectButton;
